import React from "react";
import { useNavigate } from "react-router-dom";
import { StyledLandingScreen, StyledHorizontalCarousel } from "./styles";
import {
  Button,
  Card,
  Header,
  LoaderComponent,
  BottomNav,
  ErrorScreen,
} from "../../components/index";
import { useReservations } from "../../hooks";
import { Reservation } from "@/types/reservation";
import assets from "../../assets";

type ReservationsResponse = {
  items: Reservation[];
  pagination: {
    page: number;
    items_per_page: number;
    total_items: number;
  };
};

const ReservationsScreen = () => {
  const navigate = useNavigate();
  const { reservations, loading, error } = useReservations();

  if (loading) {
    return <LoaderComponent content="Reservations loading ..." />;
  }

  if (error)
    return (
      <ErrorScreen
        content={"Please try again, later."}
        imageSrc={assets.NoResultsVector}
      />
    );

  const reservationsList =
    (reservations as unknown as ReservationsResponse)?.items || [];

  const getTodayReservations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservationsList.filter((reservation) => {
      const fromDate = new Date(reservation.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(reservation.toDate);
      toDate.setHours(0, 0, 0, 0);

      return fromDate <= today && today <= toDate;
    });
  };

  const getPastReservations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservationsList.filter((reservation) => {
      const fromDate = new Date(reservation.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(reservation.toDate);
      toDate.setHours(0, 0, 0, 0);

      return fromDate < today && toDate < today;
    });
  };

  const getUpcomingReservations = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return reservationsList.filter((reservation) => {
      const fromDate = new Date(reservation.fromDate);
      fromDate.setHours(0, 0, 0, 0);

      const toDate = new Date(reservation.toDate);
      toDate.setHours(0, 0, 0, 0);

      return fromDate > today && toDate > today;
    });
  };

  const getListingTitle = (reservation: Reservation) => {
    const allocatedUnit = reservation.allocatedUnits?.[0];
    return allocatedUnit?.listingTitle || allocatedUnit?.name;
  };

  const getMainImageFromAllocatedUnits = (reservation: Reservation) => {
    const allocatedUnit = reservation.allocatedUnits?.[0];

    if (allocatedUnit?.mainImage?.image) {
      var imageUrl = allocatedUnit.mainImage.image.urls?.big;
      if (typeof imageUrl === "string") {
        imageUrl = imageUrl.replace("https://", "http://");
      }
      return imageUrl;
    } else {
      return "";
    }
  };

  const getNumOfNights = (reservation: Reservation) => {
    return Math.floor(
      (Date.parse(reservation.toDate) - Date.parse(reservation.fromDate)) /
        86400000
    );
  };

  const getNumOfAdults = (reservation: Reservation) => {
    return reservation.adults.toString();
  };

  const getNumOfChildren = (reservation: Reservation) => {
    return reservation.children.toString();
  };

  const getStatus = (reservation: Reservation) => {
    if (reservation.confirmed === true) {
      return "Confirmed";
    } else {
      return "Not confirmed";
    }
  };

  return (
    <StyledLandingScreen>
      <Header />
      <div style={{ height: 40 }}></div>
      <Button
        style={{
          marginTop: "-27px",
          borderRadius: "25px",
          height: 50,
          fontSize: "16px",
        }}
        label="Add Reservation"
        onClick={() => navigate("/add-reservation")}
      />
      <div>
        <h2>Current Reservations</h2>
        {getTodayReservations().length === 0 && (
          <p>You have no current reservations.</p>
        )}
        <StyledHorizontalCarousel>
          {getTodayReservations().length > 0 &&
            getTodayReservations().map((reservation: Reservation) => (
              <Card
                key={reservation.uid}
                imageSrc={getMainImageFromAllocatedUnits(reservation) || ""}
                variant="main"
                onClick={() => navigate(`/reservation/${reservation.uid}`)}
                unitName={getListingTitle(reservation)}
                numNights={getNumOfNights(reservation)}
                numAdults={getNumOfAdults(reservation)}
                numChildren={getNumOfChildren(reservation)}
                fullWidth={getTodayReservations().length === 1}
                status={getStatus(reservation)}
              />
            ))}
        </StyledHorizontalCarousel>
      </div>
      <div>
        <h2>Upcoming Reservations</h2>
        {getUpcomingReservations().length === 0 && (
          <p>You have no upcoming reservations.</p>
        )}
        <StyledHorizontalCarousel>
          {getUpcomingReservations().length > 0 &&
            getUpcomingReservations().map((reservation: Reservation) => (
              <Card
                key={reservation.uid}
                imageSrc={getMainImageFromAllocatedUnits(reservation) || ""}
                variant="main"
                onClick={() => navigate(`/reservation/${reservation.uid}`)}
                unitName={getListingTitle(reservation)}
                numNights={getNumOfNights(reservation)}
                numAdults={getNumOfAdults(reservation)}
                numChildren={getNumOfChildren(reservation)}
                fullWidth={getUpcomingReservations().length === 1}
                status={getStatus(reservation)}
              />
            ))}
        </StyledHorizontalCarousel>
      </div>

      <div>
        <h2>Past Reservations</h2>
        {getPastReservations().length === 0 && (
          <p>You have no past reservations.</p>
        )}
        <StyledHorizontalCarousel>
          {getPastReservations().length > 0 &&
            getPastReservations().map((reservation: Reservation) => (
              <Card
                key={reservation.uid}
                imageSrc={getMainImageFromAllocatedUnits(reservation) || ""}
                variant="support"
                onClick={() => navigate(`/reservation/${reservation.uid}`)}
                unitName={getListingTitle(reservation)}
                numNights={getNumOfNights(reservation)}
                fullWidth={getPastReservations().length === 1}
              />
            ))}{" "}
        </StyledHorizontalCarousel>
      </div>
      <BottomNav />
    </StyledLandingScreen>
  );
};

export default ReservationsScreen;
